/**
 * STEP — Auth Store (Zustand)
 * Manages authentication state: user, tokens, tenant info.
 * Persists to localStorage for session continuity.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getTenantSlug } from "@/lib/tenant";

export type UserRole = "OWNER" | "MANAGER" | "CASHIER";

export interface AuthUser {
  id: string;
  email: string;
  phone?: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: UserRole;
  custom_role?: string;
  store_id: string | null;
  language: string;
  timezone: string;
  avatar: string | null;
  accessible_modules?: string[] | null;
}

export interface ModuleFeatures {
  analytics?: boolean;
  advanced_analytics?: boolean;
  marketing?: boolean;
  online_orders?: boolean;
  online_shop?: boolean;
  supplier_returns?: boolean;
  supplier_analytics?: boolean;
  api_access?: boolean;
  reports?: boolean;
  advanced_reports?: boolean;
  inventory_transfers?: boolean;
  inventory_audits?: boolean;
  integrations?: boolean;
  payment_gateways?: boolean;
  sms_marketing?: boolean;
  uzum_marketplace?: boolean;
  lending_bnpl?: boolean;
  fraud_detection?: boolean;
  multi_store?: boolean;
  bulk_import?: boolean;
  receipt_builder?: boolean;
  custom_domain?: boolean;
  // Granular registry modules used for in-app gating (the full 62-key set is
  // resolved server-side; the index signature allows reading any of them).
  customers?: boolean;
  purchases?: boolean;
  finance?: boolean;
  staff?: boolean;
  activities?: boolean;
  telegram_bot?: boolean;
  services?: boolean;
  rent?: boolean;
  [key: string]: boolean | undefined;
}

export interface TenantSettings {
  allow_oversell?: boolean;
  cost_price_input_currency?: "UZS" | "USD";
  [key: string]: unknown;
}

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status?: string;
  billing_period?: string;
  currency: string;
  timezone: string;
  language: string;
  logo: string | null;
  logo_url: string | null;
  max_stores: number;
  max_users: number;
  max_products: number;
  primary_domain: string | null;
  module_features?: ModuleFeatures;
  settings?: TenantSettings;
  // Legal requisites (first-class columns; owner-editable in Settings › Company).
  legal_name?: string;
  tax_id?: string;
  legal_address?: string;
  physical_address?: string;
  ibt?: string;
  company_email?: string;
  bank_accounts?: { bank_name?: string; account_number?: string; mfo?: string }[];
  subscription_ends_at?: string | null;
  trial_ends_at?: string | null;
  subscription_starts_at?: string | null;
  grace_period_ends_at?: string | null;
  days_remaining?: number | null;
  is_in_grace_period?: boolean;
  auto_renew?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  tenant: TenantInfo | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  // Actions
  setAuth: (params: {
    user: AuthUser;
    tenant?: TenantInfo;
    accessToken: string;
    refreshToken: string;
  }) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: Partial<AuthUser>) => void;
  setTenant: (tenant: TenantInfo) => void;
  logout: () => void;
  /**
   * Returns true if the stored tenant matches the current URL subdomain slug.
   * Returns true (no mismatch) when not on a tenant subdomain (e.g. localhost, app.step.uz).
   */
  isTenantMatch: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenant: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setAuth: ({ user, tenant, accessToken, refreshToken }) => {
        set({
          user,
          tenant: tenant || null,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
        if (typeof window !== 'undefined' && window.electronAPI) {
          window.electronAPI.setAuth({
            token: accessToken,
            slug: tenant?.slug ?? '',
            storeId: user.store_id ?? '',
            userId: String(user.id),
          }).then((result) => {
            console.log('[Electron] Initial sync result:', result);
          }).catch(console.error);
          window.electronAPI.setSetting('refresh_token', refreshToken).catch(console.error);
        }
      },

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      setUser: (partial) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...partial } : null,
        })),

      setTenant: (tenant) => set({ tenant }),

      isTenantMatch: (): boolean => {
        const { isAuthenticated, tenant } = useAuthStore.getState() as AuthState;
        if (!isAuthenticated || !tenant) return true;
        const urlSlug = getTenantSlug();
        if (!urlSlug) return true;
        return tenant.slug === urlSlug;
      },

      logout: () => {
        set({
          user: null,
          tenant: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
        // Clear trusted-device credentials so a logged-out device no longer
        // short-circuits to the PIN lock screen. Keys mirror auth.ts
        // (DEVICE_TOKEN_KEY / DEVICE_PHONE_KEY); inlined here to avoid an
        // import cycle (auth.ts → axios → authStore).
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            window.localStorage.removeItem('step-device-token');
            window.localStorage.removeItem('step-device-phone');
          } catch {
            /* localStorage unavailable — ignore */
          }
        }
        if (typeof window !== 'undefined' && window.electronAPI) {
          window.electronAPI.logout().catch(console.error);
          // Also wipe the Electron-persisted device credentials so they don't
          // survive logout on disk.
          window.electronAPI.setSetting('step-device-token', '').catch(console.error);
          window.electronAPI.setSetting('step-device-phone', '').catch(console.error);
        }
      },
    }),
    {
      name: "step-auth",
      partialize: (state) => ({
        user: state.user,
        tenant: state.tenant,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// ── Cross-tab synchronization ──────────────────────────────────────────────
// The persist middleware only WRITES to localStorage; it does not react to
// changes made by OTHER tabs. Without this, a token refreshed (or a logout) in
// one tab would leave sibling tabs holding a stale access token in memory,
// causing surprise refresh races / logouts.
//
// The browser fires a `storage` event only in tabs OTHER than the one that made
// the change, so there is no feedback loop with our own `set` calls. We read the
// persisted "step-auth" payload from the event and mirror the auth fields into
// this tab's in-memory store — including the logged-out case (tokens cleared).
const STORAGE_KEY = "step-auth";

if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;

    // Key removed entirely (e.g. storage cleared) → treat as logged out.
    if (event.newValue == null) {
      useAuthStore.setState({
        user: null,
        tenant: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
      });
      return;
    }

    try {
      // zustand persist wraps state as { state: {...}, version: n }
      const parsed = JSON.parse(event.newValue) as {
        state?: Partial<AuthState>;
      };
      const next = parsed?.state;
      if (!next) return;

      const current = useAuthStore.getState();
      // Skip if nothing auth-relevant actually changed (avoids needless renders).
      if (
        current.accessToken === (next.accessToken ?? null) &&
        current.refreshToken === (next.refreshToken ?? null) &&
        current.isAuthenticated === (next.isAuthenticated ?? false)
      ) {
        return;
      }

      useAuthStore.setState({
        user: next.user ?? null,
        tenant: next.tenant ?? null,
        accessToken: next.accessToken ?? null,
        refreshToken: next.refreshToken ?? null,
        isAuthenticated: next.isAuthenticated ?? false,
      });
    } catch {
      /* malformed payload — ignore, keep current in-memory state */
    }
  });
}
